import { useEffect } from 'react';
import { apiService } from '../services/api';
import { refreshEntitlement, getCachedEntitlementStatus } from './usePlan';

// Q9 (docs/plans/2026-07-29-free-tier-cap-enforcement.md §6): "activity" for slot retention is
// an intentional visit, not background API traffic — client-throttled to once per 6h so a
// pinned idle tab (which still fires plenty of other requests) doesn't look like activity.
const THROTTLE_MS = 6 * 60 * 60 * 1000;

function heartbeatStorageKey(sub) {
  return `lnc_heartbeat_last_${sub || 'anon'}`;
}

function dueToSend(sub) {
  try {
    const last = Number(localStorage.getItem(heartbeatStorageKey(sub)) || 0);
    return Date.now() - last > THROTTLE_MS;
  } catch (error) {
    // localStorage unavailable (private mode, quota, etc.) — fail open and send; worst case is
    // an extra cheap heartbeat, not a lost one.
    return true;
  }
}

function stampSent(sub) {
  try {
    localStorage.setItem(heartbeatStorageKey(sub), String(Date.now()));
  } catch (error) {
    // ignore — throttling is best-effort, never worth surfacing to the user
  }
}

// Fire-and-forget: stamp the throttle BEFORE the network call so a slow or failing request
// can't turn into a burst of retries inside the same 6h window — "at most ~once per 6h" is the
// contract here, not "at least once".
function fireHeartbeat(sub) {
  if (!dueToSend(sub)) return;
  stampSent(sub);

  apiService.sendHeartbeat()
    .then((data) => {
      // Only refetch the plan if the server-reported status actually differs from what we have
      // cached (e.g. an 'offered' user just got claimed to 'active') — avoids a redundant GET
      // on every heartbeat when nothing changed.
      if (data && data.status && data.status !== getCachedEntitlementStatus()) {
        refreshEntitlement();
      }
    })
    .catch((error) => {
      // Never block or surface to the UI — a missed heartbeat just means the next trigger
      // (mount elsewhere, next visibility change) retries.
      console.error('useHeartbeat: POST /user/heartbeat failed', error);
    });
}

/**
 * Wire up the activity heartbeat (§6/§9, free-tier-cap-enforcement plan) for the signed-in
 * user: fires once on mount and again on every visibilitychange→visible, both client-throttled
 * to once per 6h via a localStorage timestamp keyed by sub. Pass `undefined`/`null` for a
 * signed-out user to no-op.
 */
export function useHeartbeat(sub) {
  useEffect(() => {
    if (!sub) return undefined;

    fireHeartbeat(sub);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fireHeartbeat(sub);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [sub]);
}

export default useHeartbeat;
